import { Event, EventAttributes } from './Event';
import ConditionApiClient from '../apiClients/ConditionApiClient';
import ConditionTypeFactory from './conditionProperty/ConditionTypeFactory';

export type Options = {
  index?: number,
  name?: string,
  path?: string,
  negative?: boolean,
};

export type Operator = {
  type: number,
  label: string,
  negative: boolean,
};

export interface ConditionAttributes {
  id: number,
  eventId: number,
  event: EventAttributes,
  type: number,
  operator?: number,
  options?: Options,
  position?: number,
  value: any,
  createdAt: string,
  updatedAt?: string,
  fullDescription?: string,
  originalId?: number;
  snapshot?: object;
}

export const Operator = {
  EQUAL_TYPE: 1,
  CONTAIN_TYPE: 2,
  MATCH_TYPE: 3,
  CONTAIN_PROPERTY_TYPE: 4,
  BOOLEAN_TYPE: 5,
  CLICK_TYPE: 10,
  CHANGE_TYPE: 11,
  LOAD_TYPE: 12,
};

export const CommonOperators = [
  { type: Operator.EQUAL_TYPE, label: 'equals', negative: false },
  { type: Operator.EQUAL_TYPE, label: 'does not equal', negative: true },
  { type: Operator.CONTAIN_TYPE, label: 'contains', negative: false },
  { type: Operator.CONTAIN_TYPE, label: 'does not contain', negative: true },
  { type: Operator.MATCH_TYPE, label: 'matches', negative: false },
  { type: Operator.MATCH_TYPE, label: 'does not match', negative: true },
] as Array<Operator>;

export const PresenceOperators = [
  { type: Operator.BOOLEAN_TYPE, label: 'exists', negative: false },
  { type: Operator.BOOLEAN_TYPE, label: 'does not exist', negative: true },
  { type: Operator.CLICK_TYPE, label: 'on click', negative: false },
  { type: Operator.CHANGE_TYPE, label: 'on change', negative: false },
  { type: Operator.LOAD_TYPE, label: 'on load', negative: false },
] as Array<Operator>

export class Condition {
  constructor(private readonly attributes: ConditionAttributes) {
  }

  get fullDescription(): string {
    return ConditionTypeFactory.create(this)
      .buildDescription()
      .replaceAll('[', '<span class="label label-info">')
      .replaceAll(']', '</span>');
  };

  get restoreDescription(): string {
    return ConditionTypeFactory.create(this)
      .buildDescription()
      .replaceAll('[', '')
      .replaceAll(']', '');
  };

  get id(): number {
    return this.attributes.id;
  }

  get event(): Event {
    return new Event(this.attributes.event);
  }

  get eventId(): number {
    return this.attributes.eventId;
  }

  get operator(): number {
    return this.attributes.operator ?? Operator.EQUAL_TYPE;
  }

  get options(): Options {
    return this.attributes.options ?? {};
  }

  get position(): number {
    return this.attributes.position ?? 0;
  }

  get type(): number {
    return this.attributes.type;
  }

  get value(): any {
    return this.attributes.value;
  }

  get originalId(): number|undefined {
    return this.attributes.originalId;
  }

  static async findAllByEventId(eventId: number) {
    return await this.getApiClient().provideEventConditions(eventId);
  }

  private static getApiClient(): ConditionApiClient {
    return new ConditionApiClient();
  }

  static async create(attributes: ConditionAttributes) {
    return await this.getApiClient().createCondition(attributes);
  }

  async update(attributes: ConditionAttributes): Promise<Condition> {
    return await Condition.getApiClient().updateCondition(attributes);
  }

  static async batchUpdate(attributes: ConditionAttributes[]): Promise<Array<Condition>> {
    return await this.getApiClient().updateConditions(attributes);
  }

  static async delete(conditionId: number) {
    return await this.getApiClient().deleteCondition(conditionId);
  }

  static async bulkDelete(idList: Array<number>) {
    return await this.getApiClient().bulkDeleteConditions(idList);
  }
}
